import { ContactSelectionLight } from '@/components/contact-selection/contact-selection.light';
import { ContactSelectionWoo } from '@/components/contact-selection/contact-selection.woo';
import { ContactSelectionWooDark } from '@/components/contact-selection/contact-selection.woo-dark';
import type { ContactSelectionProps, ContactType } from '@/components/contact-selection/props';
import { ErrorSidePanel } from '@/components/errors';
import { useGetPersonalInfo } from '@/hooks/api';
import { useSendCode } from '@/hooks/api/contact-validation.hooks';
import { templates, type SkinPageConfig } from '@nosinovacao/nosid-mfe-common';
import { createFileRoute } from '@tanstack/react-router';
import type { HistoryState } from '@tanstack/react-router';

export const Route = createFileRoute(
  '/(home)/home/(change-password)/_sheet/change-password/contact-selection/',
)({
  component: RouteComponent,
  errorComponent: ErrorSidePanel,
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { sendCodeAsync, isSending, captcha } = useSendCode();
  const { data: personalInfo } = useGetPersonalInfo();

  const phoneAlias = personalInfo?.UsernameAliases?.find((a) => a.Type === 'Mobile')?.Username;
  const emailAlias = personalInfo?.UsernameAliases?.find((a) => a.Type === 'Email')?.Username;

  const handleContactSelected = async (contactType: ContactType, targetId: string) => {
    if (captcha.challenge && !captcha.token) return;
    await sendCodeAsync({ targetId, type: contactType, captcha });
    void navigate({
      to: '/home/change-password/code-input',
      state: { contactType, targetId } as HistoryState,
    });
  };

  const props: ContactSelectionProps = {
    translations: {
      phone: 'Common.ContactValidation.ContactSelection.Phone',
      email: 'Common.ContactValidation.ContactSelection.Email',
    },
    phoneAlias,
    emailAlias,
    onContactSelected: (ct, tid) => void handleContactSelected(ct, tid),
    isSending,
    captchaChallenge: captcha.challenge,
    onCaptchaChange: captcha.setToken,
  };

  return templates.getSkinSelector(skinConfig(props));
}

export function skinConfig(props: ContactSelectionProps): SkinPageConfig {
  return {
    skins: [
      {
        selector: 'isWooTvTemplate',
        component: <ContactSelectionWooDark {...props} />,
      },
      {
        selector: 'isWooTemplate',
        component: <ContactSelectionWoo {...props} />,
      },
    ],
    defaultValue: <ContactSelectionLight {...props} />,
  };
}
